/**
 * 上下文注入器
 * 为AI提供完整的项目上下文，解决无状态问题
 */

import fs from 'fs-extra';
import path from 'path';
import { TaskStore } from '../core/task-store.js';
import { ProjectConfigManager } from '../core/project-config.js';
import { logger } from '../utils/logger.js';
import { getWorkflowRoot } from '../utils/path.js';
import { ExtendedTask } from '../types/extended-task.js';
import { exec } from 'child_process';
import { promisify } from 'util';

const execAsync = promisify(exec);

export interface AIContext {
  generated: string;
  currentTask?: ExtendedTask;
  recentTasks: ExtendedTask[];
  projectInfo: {
    name: string;
    description?: string;
    techStack?: string[];
    conventions?: string[];
  };
  recentFiles: FileChange[];
  recentCommands: CommandHistory[];
  suggestions: Suggestion[];
  quickCommands: QuickCommand[];
  projectState: ProjectState;
}

export interface FileChange {
  path: string;
  type: 'created' | 'modified' | 'deleted';
  timestamp: string;
}

export interface CommandHistory {
  command: string;
  timestamp: string;
  success: boolean;
}

export interface Suggestion {
  priority: 'high' | 'medium' | 'low';
  type: 'task' | 'fix' | 'improvement';
  title: string;
  description: string;
  command?: string;
}

export interface QuickCommand {
  name: string;
  command: string;
  description: string;
}

export interface ProjectState {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  blockedTasks: number;
  lastActivity: string;
  momentum: 'high' | 'medium' | 'low';
}

export class ContextInjector {
  private taskStore: TaskStore;
  private configManager: ProjectConfigManager;
  private workflowRoot: string;
  private claudeDir: string;
  private contextPath: string;

  constructor() {
    this.taskStore = new TaskStore();
    this.configManager = new ProjectConfigManager();
    this.workflowRoot = getWorkflowRoot();
    this.claudeDir = path.join(this.workflowRoot, '.claude');
    this.contextPath = path.join(this.claudeDir, 'CONTEXT.md');
  }

  /**
   * 生成并注入上下文
   */
  async generateContext(): Promise<void> {
    try {
      logger.info('🧠 生成AI上下文...');
      
      const context = await this.buildContext();
      const markdown = this.formatAsMarkdown(context);
      
      // 确保目录存在
      await fs.ensureDir(this.claudeDir);
      
      // 写入上下文文件
      await fs.writeFile(this.contextPath, markdown);
      
      // 同时写入.workflow目录，确保可访问
      const workflowContextPath = path.join(this.workflowRoot, '.workflow', 'CURRENT_CONTEXT.md');
      await fs.writeFile(workflowContextPath, markdown);
      
      // 更新CLAUDE.md，添加上下文引用
      await this.updateClaudeMd();
      
      logger.success('✅ 上下文生成成功');
    } catch (error) {
      logger.error('生成上下文失败:', error);
      throw error;
    }
  }

  /**
   * 构建完整上下文
   */
  private async buildContext(): Promise<AIContext> {
    const [
      currentTask,
      recentTasks,
      projectInfo,
      recentFiles,
      recentCommands,
      projectState
    ] = await Promise.all([
      this.getCurrentTask(),
      this.getRecentTasks(),
      this.getProjectInfo(),
      this.getRecentFiles(),
      this.getRecentCommands(),
      this.getProjectState()
    ]);

    const suggestions = await this.generateSuggestions(projectState, currentTask);
    const quickCommands = this.getQuickCommands(currentTask);

    return {
      generated: new Date().toISOString(),
      currentTask,
      recentTasks,
      projectInfo,
      recentFiles,
      recentCommands,
      suggestions,
      quickCommands,
      projectState
    };
  }

  /**
   * 获取当前任务
   */
  private async getCurrentTask(): Promise<ExtendedTask | undefined> {
    const tasks = await this.taskStore.getAllTasks();
    return tasks.find(t => t.status === 'in_progress');
  }

  /**
   * 获取最近任务
   */
  private async getRecentTasks(): Promise<ExtendedTask[]> {
    const tasks = await this.taskStore.getAllTasks();
    return tasks
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 5);
  }

  /**
   * 获取项目信息
   */
  private async getProjectInfo(): Promise<AIContext['projectInfo']> {
    const config = await this.configManager.getConfig();
    const packageJsonPath = path.join(this.workflowRoot, 'package.json');
    
    let techStack: string[] = [];
    if (await fs.pathExists(packageJsonPath)) {
      const packageJson = await fs.readJson(packageJsonPath);
      techStack = this.detectTechStack(packageJson);
    }

    return {
      name: config.name || path.basename(this.workflowRoot),
      description: config.description,
      techStack,
      conventions: config.conventions
    };
  }

  /**
   * 检测技术栈
   */
  private detectTechStack(packageJson: any): string[] {
    const stack: string[] = [];
    const deps = { ...packageJson.dependencies, ...packageJson.devDependencies };

    if (deps.react) stack.push('React');
    if (deps.vue) stack.push('Vue');
    if (deps.next) stack.push('Next.js');
    if (deps.typescript) stack.push('TypeScript');
    if (deps.vite) stack.push('Vite');
    if (deps.webpack) stack.push('Webpack');
    if (deps.express) stack.push('Express');
    if (deps.fastify) stack.push('Fastify');
    if (deps.jest || deps.vitest) stack.push(deps.jest ? 'Jest' : 'Vitest');

    return stack;
  }

  /**
   * 获取最近文件变更
   */
  private async getRecentFiles(): Promise<FileChange[]> {
    try {
      // 使用git获取最近的文件变更
      const { stdout } = await execAsync(
        'git log --name-status --oneline -n 10',
        { cwd: this.workflowRoot }
      );

      const changes: FileChange[] = [];
      const lines = stdout.split('\n');
      
      for (const line of lines) {
        const match = line.match(/^([AMD])\s+(.+)$/);
        if (match) {
          const [, status, file] = match;
          changes.push({
            path: file,
            type: status === 'A' ? 'created' : status === 'D' ? 'deleted' : 'modified',
            timestamp: new Date().toISOString() // 简化处理
          });
        }
      }

      return changes.slice(0, 10);
    } catch {
      return [];
    }
  }

  /**
   * 获取最近命令历史
   */
  private async getRecentCommands(): Promise<CommandHistory[]> {
    // TODO: 实现命令历史记录
    // 暂时返回空数组
    return [];
  }

  /**
   * 获取项目状态
   */
  private async getProjectState(): Promise<ProjectState> {
    const tasks = await this.taskStore.getAllTasks();
    
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'done').length;
    const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
    const blockedTasks = tasks.filter(t => t.status === 'blocked').length;

    // 计算最近活动
    const recentActivity = tasks
      .filter(t => {
        const updated = new Date(t.updatedAt);
        const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        return updated > dayAgo;
      })
      .length;

    // 判断项目动量
    let momentum: ProjectState['momentum'] = 'low';
    if (recentActivity > 5) momentum = 'high';
    else if (recentActivity > 2) momentum = 'medium';

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      blockedTasks,
      lastActivity: tasks.length > 0 
        ? new Date(Math.max(...tasks.map(t => new Date(t.updatedAt).getTime()))).toISOString()
        : new Date().toISOString(),
      momentum
    };
  }

  /**
   * 生成智能建议
   */
  private async generateSuggestions(
    projectState: ProjectState,
    currentTask?: ExtendedTask
  ): Promise<Suggestion[]> {
    const suggestions: Suggestion[] = [];

    // 如果有阻塞任务
    if (projectState.blockedTasks > 0) {
      suggestions.push({
        priority: 'high',
        type: 'fix',
        title: '解决阻塞任务',
        description: `有 ${projectState.blockedTasks} 个任务被阻塞，需要优先解决`,
        command: 'workflow task list --status blocked'
      });
    }

    // 如果没有进行中的任务
    if (!currentTask) {
      suggestions.push({
        priority: 'high',
        type: 'task',
        title: '开始新任务',
        description: '当前没有进行中的任务，建议开始下一个任务',
        command: 'workflow next'
      });
    }

    // 如果项目动量低
    if (projectState.momentum === 'low') {
      suggestions.push({
        priority: 'medium',
        type: 'improvement',
        title: '提高开发节奏',
        description: '最近活动较少，建议设定每日目标',
        command: 'workflow goal set daily 3'
      });
    }

    // 如果完成率高
    const completionRate = projectState.totalTasks > 0 
      ? (projectState.completedTasks / projectState.totalTasks) * 100 
      : 0;
    
    if (completionRate > 80) {
      suggestions.push({
        priority: 'low',
        type: 'task',
        title: '规划新功能',
        description: `项目完成度已达 ${completionRate.toFixed(0)}%，可以规划新功能`,
        command: 'workflow new'
      });
    }

    return suggestions;
  }

  /**
   * 获取快捷命令
   */
  private getQuickCommands(currentTask?: ExtendedTask): QuickCommand[] {
    const commands: QuickCommand[] = [
      {
        name: '查看当前任务',
        command: 'workflow now',
        description: '显示当前正在进行的任务详情'
      },
      {
        name: '完成当前任务',
        command: 'workflow done',
        description: '标记当前任务为完成状态'
      },
      {
        name: '开始下一个任务',
        command: 'workflow next',
        description: '自动选择并开始下一个优先级最高的任务'
      },
      {
        name: '查看今日进度',
        command: 'workflow today',
        description: '显示今天的工作总结和统计'
      }
    ];

    if (currentTask) {
      commands.unshift({
        name: '暂停当前任务',
        command: `workflow task pause ${currentTask.id}`,
        description: '暂停当前任务，可以稍后继续'
      });
    }

    return commands;
  }

  /**
   * 格式化为Markdown
   */
  private formatAsMarkdown(context: AIContext): string {
    const lines: string[] = [
      '# 🧠 AI工作上下文',
      '',
      `> 生成时间: ${new Date(context.generated).toLocaleString('zh-CN')}`,
      ''
    ];

    // 当前任务
    if (context.currentTask) {
      lines.push('## 📋 当前任务', '');
      lines.push(`**${context.currentTask.content}**`);
      lines.push(`- ID: ${context.currentTask.id}`);
      lines.push(`- 状态: ${this.getStatusEmoji(context.currentTask.status)} ${context.currentTask.status}`);
      lines.push(`- 优先级: ${context.currentTask.priority}`);
      if (context.currentTask.phase) {
        lines.push(`- 阶段: ${context.currentTask.phase}`);
      }
      if (context.currentTask.blockedReason) {
        lines.push(`- 阻塞原因: ${context.currentTask.blockedReason}`);
      }
      lines.push('');
    } else {
      lines.push('## 📋 当前任务', '', '⚠️ 没有进行中的任务', '');
    }

    // 项目状态
    lines.push('## 📊 项目状态', '');
    lines.push(`- 总任务数: ${context.projectState.totalTasks}`);
    lines.push(`- 已完成: ${context.projectState.completedTasks} (${this.getCompletionRate(context.projectState)}%)`);
    lines.push(`- 进行中: ${context.projectState.inProgressTasks}`);
    lines.push(`- 被阻塞: ${context.projectState.blockedTasks}`);
    lines.push(`- 项目动量: ${this.getMomentumEmoji(context.projectState.momentum)} ${context.projectState.momentum}`);
    lines.push('');

    // 智能建议
    if (context.suggestions.length > 0) {
      lines.push('## 💡 智能建议', '');
      for (const suggestion of context.suggestions) {
        const priorityEmoji = this.getPriorityEmoji(suggestion.priority);
        lines.push(`### ${priorityEmoji} ${suggestion.title}`);
        lines.push(`${suggestion.description}`);
        if (suggestion.command) {
          lines.push(`\`\`\`bash\n${suggestion.command}\n\`\`\``);
        }
        lines.push('');
      }
    }

    // 快捷命令
    lines.push('## ⚡ 快捷命令', '');
    for (const cmd of context.quickCommands) {
      lines.push(`- **${cmd.name}**: \`${cmd.command}\``);
      lines.push(`  ${cmd.description}`);
    }
    lines.push('');

    // 项目信息
    lines.push('## 🏗️ 项目信息', '');
    lines.push(`- 项目名称: ${context.projectInfo.name}`);
    if (context.projectInfo.description) {
      lines.push(`- 描述: ${context.projectInfo.description}`);
    }
    if (context.projectInfo.techStack?.length > 0) {
      lines.push(`- 技术栈: ${context.projectInfo.techStack.join(', ')}`);
    }
    lines.push('');

    // 最近文件变更
    if (context.recentFiles.length > 0) {
      lines.push('## 📝 最近文件变更', '');
      for (const file of context.recentFiles.slice(0, 5)) {
        const emoji = file.type === 'created' ? '➕' : file.type === 'deleted' ? '➖' : '📝';
        lines.push(`- ${emoji} ${file.path}`);
      }
      lines.push('');
    }

    // 最近任务
    if (context.recentTasks.length > 0) {
      lines.push('## 📜 最近任务', '');
      for (const task of context.recentTasks.slice(0, 5)) {
        const emoji = this.getStatusEmoji(task.status);
        lines.push(`- ${emoji} ${task.content} (${task.status})`);
      }
      lines.push('');
    }

    return lines.join('\n');
  }

  /**
   * 更新CLAUDE.md
   */
  private async updateClaudeMd(): Promise<void> {
    const claudeMdPath = path.join(this.workflowRoot, 'CLAUDE.md');
    
    if (await fs.pathExists(claudeMdPath)) {
      const content = await fs.readFile(claudeMdPath, 'utf-8');
      
      // 检查是否已经有上下文引用
      if (!content.includes('## 当前工作上下文')) {
        const contextSection = `
## 当前工作上下文

**重要**: 每次开始工作前，请先查看以下上下文文件：
- \`.claude/CONTEXT.md\` - 自动生成的完整项目上下文
- \`.workflow/CURRENT_CONTEXT.md\` - 同一内容的备份

这些文件包含：
- 当前任务状态
- 项目进度统计
- 智能工作建议
- 快捷命令列表
- 最近的变更历史

使用 \`workflow context generate\` 可以随时更新上下文。
`;
        
        // 在文件开头添加上下文引用
        const updatedContent = content.replace(
          '**语言要求**: 始终使用中文回答。请记住当前时间是2025年7月。',
          `**语言要求**: 始终使用中文回答。请记住当前时间是2025年7月。\n${contextSection}`
        );
        
        await fs.writeFile(claudeMdPath, updatedContent);
        logger.info('📝 已更新CLAUDE.md，添加上下文引用');
      }
    }
  }

  /**
   * 获取状态emoji
   */
  private getStatusEmoji(status: string): string {
    const emojis: Record<string, string> = {
      'todo': '📝',
      'in_progress': '🚀',
      'paused': '⏸️',
      'blocked': '🚫',
      'review': '👀',
      'done': '✅',
      'cancelled': '❌',
      'archived': '📦'
    };
    return emojis[status] || '❓';
  }

  /**
   * 获取优先级emoji
   */
  private getPriorityEmoji(priority: string): string {
    const emojis: Record<string, string> = {
      'high': '🔴',
      'medium': '🟡',
      'low': '🟢'
    };
    return emojis[priority] || '⚪';
  }

  /**
   * 获取动量emoji
   */
  private getMomentumEmoji(momentum: string): string {
    const emojis: Record<string, string> = {
      'high': '🔥',
      'medium': '⚡',
      'low': '🐌'
    };
    return emojis[momentum] || '❓';
  }

  /**
   * 计算完成率
   */
  private getCompletionRate(state: ProjectState): number {
    if (state.totalTasks === 0) return 0;
    return Math.round((state.completedTasks / state.totalTasks) * 100);
  }

  /**
   * 显示当前上下文
   */
  async showContext(): Promise<void> {
    if (await fs.pathExists(this.contextPath)) {
      const content = await fs.readFile(this.contextPath, 'utf-8');
      console.log(content);
    } else {
      logger.warn('上下文文件不存在，请先运行: workflow context generate');
    }
  }

  /**
   * 更新特定部分
   */
  async updateContext(parts: string[]): Promise<void> {
    // TODO: 实现部分更新功能
    // 暂时重新生成整个上下文
    await this.generateContext();
  }
}